/* ───────────────────────────────────────────────────────────────────
   vd-cro-hero — auto-rotating hero (gummies ⇄ capsules)

   • Toggles data-state on the section root every N ms (configured per
     instance via data-interval; default 3000).
   • Pauses when: prefers-reduced-motion, hover, focus inside, off-screen
     (IntersectionObserver), or tab hidden.
   • WCAG 2.2 SC 2.2.2 — exposes a Play/Pause button.
   • Built OOP-style so multiple instances on a page are safe even though
     the schema currently uses limit: 1.
   ─────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var STATES = ['gummies', 'capsules'];
  var ANNOUNCE = {
    gummies:  'Now showing Hair Growth Peptides Gummies.',
    capsules: 'Now showing Hair Growth Peptides Capsules.'
  };

  function VdCroHero(root) {
    if (!root || root._vdCroHero) return;
    root._vdCroHero = this;

    this.root      = root;
    this.live      = root.querySelector('[data-vd-cro-hero-live]');
    this.ppBtn     = root.querySelector('[data-vd-cro-hero-playpause]');
    this.interval  = parseInt(root.getAttribute('data-interval'), 10) || 3000;
    this.timer     = null;
    this.playing   = false;
    this.reduce    = window.matchMedia &&
                     window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.bind();
    this.observe();

    if (!this.reduce) this.start();
  }

  VdCroHero.prototype.next = function () {
    var current = this.root.getAttribute('data-state') || STATES[0];
    var idx     = STATES.indexOf(current);
    var nextIdx = (idx + 1) % STATES.length;
    this.setState(STATES[nextIdx]);
  };

  VdCroHero.prototype.setState = function (state) {
    if (STATES.indexOf(state) === -1) return;
    if (this.root.getAttribute('data-state') === state) return;
    this.root.setAttribute('data-state', state);
    // Automatic (timer-driven) swaps do NOT announce: the images are decorative
    // and a "Now showing…" every few seconds spams screen readers. Announcements
    // fire only on a user action — see announce(), called from the Play/Pause
    // button (a deliberate, infrequent gesture).
  };

  // Announce the currently shown product to the live region. Called only for
  // user-initiated changes so the decorative auto-rotation stays silent.
  VdCroHero.prototype.announce = function () {
    var state = this.root.getAttribute('data-state');
    if (!this.live || !ANNOUNCE[state]) return;
    this.live.textContent = '';
    var live = this.live;
    var msg  = ANNOUNCE[state];
    requestAnimationFrame(function () { live.textContent = msg; });
  };

  VdCroHero.prototype.start = function () {
    if (this.reduce || this.timer) return;
    this.playing = true;
    this.updatePPBtn();
    var self = this;
    this.timer = setInterval(function () { self.next(); }, this.interval);
  };

  VdCroHero.prototype.pause = function () {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.playing = false;
    this.updatePPBtn();
  };

  VdCroHero.prototype.togglePlay = function () {
    if (this.playing) {
      this.pause();
      // User deliberately paused — announce the product now frozen on screen.
      // (Auto-pauses from hover/focus/off-screen/tab-hidden call pause() directly
      // and stay silent.)
      this.announce();
    } else {
      this.start();
    }
  };

  VdCroHero.prototype.updatePPBtn = function () {
    if (!this.ppBtn) return;
    this.ppBtn.setAttribute('aria-pressed', String(this.playing));
    this.ppBtn.setAttribute(
      'aria-label',
      this.playing ? 'Pause hero slideshow' : 'Play hero slideshow'
    );
  };

  VdCroHero.prototype.bind = function () {
    var self = this;

    if (this.ppBtn) {
      this.ppBtn.addEventListener('click', function () { self.togglePlay(); });
    }

    /* Pause on hover */
    this.root.addEventListener('mouseenter', function () {
      if (self.playing) self.pause();
    });
    this.root.addEventListener('mouseleave', function () {
      if (!self.reduce && !self.playing && self.inView !== false) self.start();
    });

    /* Pause when keyboard focus enters the hero */
    this.root.addEventListener('focusin', function () {
      if (self.playing) self.pause();
    });
    this.root.addEventListener('focusout', function (e) {
      if (!self.root.contains(e.relatedTarget) &&
          !self.reduce && !self.playing && self.inView !== false) {
        self.start();
      }
    });

    /* Pause when the tab is backgrounded (saves CPU + image work) */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        if (self.playing) self.pause();
      } else if (!self.reduce && self.inView !== false) {
        self.start();
      }
    });
  };

  VdCroHero.prototype.observe = function () {
    if (!('IntersectionObserver' in window)) {
      this.inView = true;
      return;
    }
    var self = this;
    this.inView = true;
    this.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        self.inView = entry.isIntersecting;
        if (entry.isIntersecting) {
          if (!self.reduce && !document.hidden) self.start();
        } else {
          self.pause();
        }
      });
    }, { threshold: 0.2 });
    this.io.observe(this.root);
  };


  

  function init(scope) {
    (scope || document)
      .querySelectorAll('[data-vd-cro-hero]')
      .forEach(function (el) { new VdCroHero(el); });
  }

  /* Primary button → smooth-scroll to an in-page target (e.g. #home-buy-offer),
     using the button's own data-top / data-top-mobile offset (mirrors the landing
     pages' scrollToOffer, but self-contained so it works on any layout). Falls back
     to the target element's data-top if the button has none. */
  function smoothScrollAnchor(a, event) {
    var isMobile = window.matchMedia('(max-width: 989px)').matches;
    var target = null;

    // On MOBILE, an optional class-based target takes priority — lets the button
    // scroll to a different section on mobile than on desktop. Accepts ".class",
    // "class" or "#id"; first match wins.
    if (isMobile) {
      var mobSel = (a.getAttribute('data-scroll-target-mobile') || '').trim();
      if (mobSel) {
        if (mobSel.charAt(0) !== '.' && mobSel.charAt(0) !== '#') mobSel = '.' + mobSel;
        try { target = document.querySelector(mobSel); } catch (e) { target = null; }
      }
    }

    // Fallback (desktop, or no mobile target): the id from data-scroll-target
    // (a plain id, set via a text setting so it isn't subject to the URL field's
    // validation), else from a #anchor href.
    if (!target) {
      var targetId = (a.getAttribute('data-scroll-target') || '').replace(/^#/, '').trim();
      if (!targetId) {
        var href = a.getAttribute('href') || '';
        if (href.charAt(0) === '#' && href.length > 1) targetId = href.slice(1);
      }
      if (targetId) target = document.getElementById(targetId);
    }

    if (!target) return;

    // On mobile, when the CTA scrolls to the buy-box, always land on its purchase
    // tabs (GUMMIES/CAPSULES) rather than the top of the gallery — so the user
    // lands on the plan selector. Works even if the target resolved to the whole
    // buy-box section. If the target already IS the tabs, this is a no-op.
    if (isMobile && target && !(target.classList && target.classList.contains('vdhb__tabs'))) {
      var tabsInside = target.querySelector && target.querySelector('.vdhb__tabs');
      if (tabsInside) target = tabsInside;
    }

    if (event) event.preventDefault();

    // A11y: move keyboard/AT focus into the offer too. Native fragment nav and
    // our custom scroll only move the viewport, not focus — so screen-reader
    // users would be left on the hero with no idea the context changed. Prefer
    // the buy-box plan tabs (the variant selector, same landing spot as the
    // skip link); fall back to the first real control, then the target itself.
    // preventScroll:true so the rAF glide below still owns the visual motion.
    (function moveFocusIntoTarget() {
      var dest = null;
      var tabs = (target.classList && target.classList.contains('vdhb__tabs'))
        ? target
        : (target.querySelector && target.querySelector('.vdhb__tabs'));
      if (tabs) {
        dest = tabs.querySelector('[role="tab"][aria-selected="true"]') ||
               tabs.querySelector('[role="tab"], button');
      }
      if (!dest && target.querySelectorAll) {
        var cands = target.querySelectorAll('a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]');
        for (var i = 0; i < cands.length; i++) {
          var c = cands[i];
          if (c.classList && c.classList.contains('vdhb__skip-link')) continue;
          if (c.tabIndex >= 0 && c.offsetParent !== null) { dest = c; break; }
        }
      }
      if (!dest) {
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        dest = target;
      }
      try { dest.focus({ preventScroll: true }); } catch (e) { dest.focus(); }
    }());

    // Space to leave above the target. When the GLP header is present (VD landing
    // layouts) it auto-hides on any scroll away from the top, so no space is
    // reserved and the target lands flush at the top. Otherwise measure the
    // theme's own sticky header (.header-wpr) so the target lands right beneath
    // it; fall back to the button's / target's configured data-top offset.
    function headerOffset() {
      if (document.querySelector('.section-vd-glp-header')) {
        return 0;
      }
      var themeHeader = document.querySelector('.header-wpr');
      if (themeHeader) {
        var h = themeHeader.getBoundingClientRect().height;
        if (h) return h;
      }
      var attr = isMobile ? a.getAttribute('data-top-mobile') : a.getAttribute('data-top');
      if (attr == null || attr === '') {
        attr = isMobile ? target.getAttribute('data-top-mobile') : target.getAttribute('data-top');
      }
      return parseInt(attr, 10) || 0;
    }

    // Custom rAF scroll instead of native scrollIntoView({behavior:'smooth'}).
    //
    // Why: on the first click after a refresh, lazy-loaded images/sections ABOVE
    // the target are still loading, so the target keeps moving down while we scroll.
    // The native smooth scroll locks onto a fixed destination, so we'd land short —
    // and re-issuing scrollIntoView to correct it RESTARTS the easing mid-flight,
    // which is the visible "stutter".
    //
    // This loop instead re-reads the target's LIVE position every frame and eases
    // the current scroll toward it (chase easing: move a fraction of the remaining
    // distance each frame). When lazy content shifts the target, the motion simply
    // keeps gliding to the new spot in ONE continuous animation — no restart, no
    // stutter — and it always lands accurately. Bails the instant the user scrolls.
    var reduce = window.matchMedia &&
                 window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function destNow() {
      // Absolute Y that puts the target's top right below the sticky header.
      return target.getBoundingClientRect().top + window.pageYOffset - headerOffset();
    }

    var EASE = 0.20;        // chase fraction per 60fps frame — higher = faster glide
    var RAMP_MS = 260;      // ease-in window at the start so it accelerates from rest (smoothness)
    var SETTLE_PX = 1.5;    // "arrived" tolerance
    var STABLE_MS = 350;    // must stay arrived this long before we stop (catches late loads)
    var MAX_MS = 3000;      // hard cap
    var startTs = null;
    var lastTs = null;
    var stableTs = null;
    var cancelled = false;

    // The theme sets `html { scroll-behavior: smooth }`, which would make EACH of
    // our per-frame scrollTo() calls launch its own native smooth scroll — they
    // fight each other and the page freezes. Force instant scrolling for the
    // duration of our animation, then restore whatever was there.
    var rootStyle = document.documentElement.style;
    var prevScrollBehavior = rootStyle.scrollBehavior;
    rootStyle.scrollBehavior = 'auto';

    // Suppress the GLP sticky-header reveal while this button-driven scroll runs
    // (the header logic lives in vd-glp.js and reads this shared flag).
    window.__vdgProgrammaticScroll = true;

    function cleanup() {
      rootStyle.scrollBehavior = prevScrollBehavior;
      window.__vdgProgrammaticScroll = false;
      window.removeEventListener('wheel', onUser);
      window.removeEventListener('touchmove', onUser);
      window.removeEventListener('keydown', onUser);
    }
    function onUser() { cancelled = true; cleanup(); }
    window.addEventListener('wheel', onUser, { passive: true });
    window.addEventListener('touchmove', onUser, { passive: true });
    window.addEventListener('keydown', onUser);

    function step(ts) {
      if (cancelled) return;
      if (startTs === null) startTs = ts;
      if (lastTs === null) lastTs = ts;
      var dt = Math.min(ts - lastTs, 50); // clamp big gaps (e.g. tab refocus)
      lastTs = ts;

      var current = window.pageYOffset;
      var dest = destNow();
      var diff = dest - current;

      // Frame-rate-independent chase easing: EASE is calibrated per 60fps frame and
      // rescaled to the real frame delta, so the glide stays smooth and consistent on
      // any display (60/90/120Hz) instead of speeding up on faster refresh rates.
      // A short quadratic ease-in ramp accelerates from rest (no abrupt first jump);
      // the chase itself eases out at the end → smooth ease-in-out feel.
      var frac = 1 - Math.pow(1 - EASE, dt / 16.67);
      var ramp = Math.min(1, (ts - startTs) / RAMP_MS);
      frac *= ramp * ramp;

      // Reduced motion: snap straight to the live target each frame (no animation),
      // but keep tracking so late-loading content above still lands us correctly.
      var next = reduce ? dest : current + diff * frac;
      if (Math.abs(diff) < SETTLE_PX) next = dest;
      window.scrollTo(0, next);

      if (Math.abs(diff) < SETTLE_PX) {
        if (stableTs === null) stableTs = ts;
        if (ts - stableTs >= STABLE_MS) { cleanup(); return; } // arrived & stayed put
      } else {
        stableTs = null; // moved again (content shifted) — keep chasing
      }

      if (ts - startTs >= MAX_MS) { cleanup(); return; }
      requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('.vd-cro-hero__btn--primary');
    if (a) smoothScrollAnchor(a, e);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Shopify theme editor re-init on section load */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
}());
